/**
 * Per-account persisted high-water marks — the replay memory both signed
 * records and sealed envelopes rely on.
 *
 * Existed as two near-line-for-line copies (signedRecord `marks`,
 * sealedRecordStore `latestEnvelopes`) with the same invariants:
 *
 * - PERSISTED to IndexedDB, because an in-memory mark dies with the tab and an
 *   attacker rolling a record back merely has to wait for a reload.
 * - Keyed per ACCOUNT, and the account it describes is tracked rather than
 *   assumed: one user's view of "latest" must never gate another's reads on a
 *   shared device — and the persist must never write one account's marks under
 *   another's key.
 * - Loads are MEMOIZED: reads that start together await the SAME load rather
 *   than the first claiming the account and the rest racing past an empty map,
 *   which would let a replay through on exactly the cold load persistence
 *   exists to cover.
 * - Marks merge FORWARD only, both from disk (a mark seen this session already
 *   outranks disk) and via {@link MarkStore.advance} (a concurrent higher mark
 *   must not be regressed by a lower one that finished hashing later).
 */

import { idbGet, idbSet } from './support/idbCache';

export type SeqMark = { seq: number; hash: string };

export type MarkStore = {
  get(key: string): SeqMark | undefined;
  /** Set the mark ONLY if it advances past the one held. Advance-only, always. */
  advance(key: string, mark: SeqMark): void;
  has(key: string): boolean;
  /** Load (or switch to) `pub`'s persisted marks. Memoized per account. */
  load(pub: string): Promise<void>;
  /** Forget everything (logout, account switch, tests). */
  reset(): void;
};

export function createPersistedMarkStore(storagePrefix: string): MarkStore {
  const marks = new Map<string, SeqMark>();
  let loadedPub: string | null = null;
  let loadPromise: Promise<void> | null = null;
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Bumped by every {@link reset}. A load captures it before awaiting and drops
   * its result if it no longer matches.
   *
   * Clearing the map is not enough to abandon a load: the `await idbGet` of the
   * OUTGOING account is still in flight, and it resolves into whatever map
   * exists when it lands. So an account switch mid-load merged A's marks into
   * B's map — gating B's reads with a stranger's high-water marks, and (once
   * anything called `advance`) persisting them under B's key. Exactly the two
   * failures the per-account keying exists to prevent.
   */
  let generation = 0;

  const storeKey = (pub: string) => `${storagePrefix}${pub}`;

  const schedulePersist = () => {
    if (persistTimer || !loadedPub) return;
    persistTimer = setTimeout(() => {
      persistTimer = null;
      const pub = loadedPub;
      if (!pub) return;
      // Best effort: a device that cannot persist falls back to in-session-only
      // replay protection, which is strictly better than none.
      void idbSet(storeKey(pub), Object.fromEntries(marks)).catch(() => {});
    }, 500);
  };

  const reset = () => {
    generation++;
    marks.clear();
    loadedPub = null;
    loadPromise = null;
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
  };

  return {
    get: (key) => marks.get(key),
    has: (key) => marks.has(key),

    advance(key, mark) {
      const known = marks.get(key);
      if (known && known.seq >= mark.seq) return;
      marks.set(key, mark);
      schedulePersist();
    },

    load(pub: string): Promise<void> {
      if (!pub) return Promise.resolve();
      if (loadedPub === pub && loadPromise) return loadPromise;
      // A different account than the one loaded: drop its marks first — a
      // merged map both gates this account's reads with a stranger's marks and
      // persists them under this account's key.
      if (loadedPub && loadedPub !== pub) reset();
      loadedPub = pub;
      const gen = generation;
      loadPromise = (async () => {
        try {
          const stored = await idbGet<Record<string, SeqMark>>(storeKey(pub), {});
          // Superseded while we were reading: this is the outgoing account's
          // data and it must not touch the map that replaced it.
          if (gen !== generation) return;
          for (const [key, mark] of Object.entries(stored || {})) {
            if (!mark || typeof mark.seq !== 'number' || typeof mark.hash !== 'string') continue;
            const known = marks.get(key);
            if (known && known.seq >= mark.seq) continue;
            marks.set(key, mark);
          }
        } catch {
          /* in-session protection still applies */
        }
      })();
      return loadPromise;
    },

    reset,
  };
}
